/**
 * tile-map viewer config — DERIVED from the active ProjectConfig.
 *
 * Formerly this file was a second, tscheppa-hardcoded config
 * (`tscheppaTileMapConfig`) next to the tenant registry in project.ts —
 * a new tenant needed BOTH files. Since the tenant-template merge
 * (Post 4242, 2026-07-15) everything lives in projects/<tenant>.ts:
 * identity/api/track come from the ProjectConfig itself, viewer-specific
 * extras (calibration, physics, overlays, kiosk, …) from its optional
 * `tileMap` block. One tenant = ONE file.
 */
import type { ArtrackWebConfig } from '@apopovic77/tile-map'
import { getConfig, type ProjectConfig } from './project'

// Derive the field types from ArtrackWebConfig itself — the library's index
// ALSO exports an unrelated `MapConfig` (physics map types), so importing
// by name would bind the wrong type.
type MapCfg = ArtrackWebConfig['map']
type ViewCfg = ArtrackWebConfig['view']

/** Kachel-Variante zum Vergleichen (Testhost): `?kacheln=<name>` lädt
 *  /tiles/manifest.<name>.json statt des Standard-Manifests. Ohne Parameter
 *  oder bei ungültigem Namen bleibt alles wie konfiguriert. */
function mitKachelVariante(map: MapCfg): MapCfg {
  if (typeof window === 'undefined' || map.type !== 'tiles') return map
  const name = new URLSearchParams(window.location.search).get('kacheln')
  if (!name || !/^[a-z0-9_]+$/.test(name)) return map
  return { ...map, manifestPath: `/tiles/manifest.${name}.json` }
}

/** Build the viewer config for a project. Pure — no singleton state. */
export function buildTileMapConfig(p: ProjectConfig): ArtrackWebConfig {
  const { map: extraMap, view: extraView, ...extras } = p.tileMap ?? {}

  // Viewer map source: the project's tileMap block wins (full detail);
  // otherwise synthesize from the coarse ProjectConfig.map so projects
  // without extras (e.g. makerspace, type 'blank') still boot the viewer.
  const map: MapCfg = extraMap ?? (p.map.type === 'tiles' && p.map.tiles
    ? {
        type: 'tiles',
        manifestPath: p.map.tiles.manifest,
        tilesBasePath: '/tiles',
        calibrationPath: p.map.tiles.calibration,
      }
    : { type: 'blank', backgroundColor: p.map.backgroundColor })

  const view: ViewCfg = extraView ?? {
    center: p.map.center,
    defaultZoom: p.map.defaultZoom,
    backgroundColor: p.map.backgroundColor,
  }

  return {
    projectId: p.id,
    projectName: p.name,
    api: {
      artrackUrl: p.api.artrackUrl,
      storageUrl: p.api.storageUrl,
      knowledgeUrl: p.api.knowledgeUrl,
      apiKey: p.api.apiKey,
      trackId: p.api.trackId,
      defaultRouteId: p.defaultRouteId,
    },
    map: mitKachelVariante(map),
    view,
    ...extras,
  }
}

/** The active tenant's viewer config (resolved once, like getConfig()). */
export const tileMapConfig: ArtrackWebConfig = buildTileMapConfig(getConfig())

/** @deprecated Tenant-agnostic now — import `tileMapConfig` instead. Kept so
 *  in-flight branches referencing the old name keep compiling. */
export const tscheppaTileMapConfig = tileMapConfig
